import React from "react";
import { Link } from "react-router-dom";
import { BookOpen, Clock } from "lucide-react";

// The API sends duration_hours; older mock data used durationHours.
function formatDuration(hours) {
    const total = Math.round((Number(hours) || 0) * 60);
    if (!total) return null;
    const h = Math.floor(total / 60);
    const m = total % 60;
    return h ? (m ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
}

export default function CourseCard({ course, showProgress = false }) {
    const duration = formatDuration(course.duration_hours ?? course.durationHours);
    const instructor = course.instructor || {
        name: course.instructor_name || "",
        title: course.instructor_title || "",
        avatar: course.instructor_avatar || "",
    };
    const progress = Number(course.progress) || 0;
    return (
        <Link
            to={course.enrolled || showProgress ? `/learn/${course.id}` : `/catalog/${course.id}`}
            data-testid={`course-card-${course.id}`}
            className="group flex flex-col bg-ink-700 rounded-2xl border border-white/5 overflow-hidden hover:border-white/15 transition-all hover:-translate-y-1"
        >
            <div className="relative aspect-[4/3] overflow-hidden bg-ink-600">
                <img
                    src={course.thumbnail}
                    alt={course.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-ink-900/80 backdrop-blur-sm text-xs font-semibold text-white border border-white/10">
                    {course.category}
                </span>
            </div>

            <div className="p-5 flex-1 flex flex-col">
                <h3 className="font-display font-bold text-white text-lg leading-tight line-clamp-2 group-hover:text-lime transition-colors">
                    {course.title}
                </h3>

                <div className="mt-4 flex items-center justify-between text-xs text-gray-400">
                    {course.level && (
                        <span className="flex items-center gap-1.5">
                            <BookOpen className="w-3.5 h-3.5" />
                            {course.level}
                        </span>
                    )}
                    {duration && (
                        <span className="flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5" />
                            {duration}
                        </span>
                    )}
                </div>

                {showProgress ? (
                    <div className="mt-5 pt-4 border-t border-white/5">
                        <div className="flex items-center justify-between text-xs mb-2">
                            <span className="text-gray-400">Progress</span>
                            <span className="text-lime font-semibold">{progress}%</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-ink-600 overflow-hidden">
                            <div
                                className="h-full rounded-full bg-gradient-to-r from-grape to-lime"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="mt-5 pt-4 border-t border-white/5 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                            {instructor.avatar && (
                                <img src={instructor.avatar} alt={instructor.name} className="w-7 h-7 rounded-full object-cover shrink-0" />
                            )}
                            <div className="min-w-0">
                                <div className="text-xs font-semibold text-white truncate">{instructor.name}</div>
                                <div className="text-[10px] text-gray-500 truncate">{instructor.title}</div>
                            </div>
                        </div>
                        <span className="px-4 py-1.5 rounded-full bg-lime text-ink-900 text-xs font-bold shrink-0">
                            Enroll
                        </span>
                    </div>
                )}
            </div>
        </Link>
    );
}
